<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">军人职业发展</h1>
      <p class="page-subtitle">Military Career Development</p>
    </div>
    
    <div class="main-content">
      <aside class="sidebar">
        <div class="sidebar-header">
          <h3 class="sidebar-title">🎖️ 发展阶段</h3>
        </div>
        <nav class="sidebar-nav">
          <button 
            v-for="stage in careerStages" 
            :key="stage.value"
            @click="currentStage = stage.value"
            :class="['nav-item', { active: currentStage === stage.value }]"
          >
            <span class="nav-icon">{{ stage.icon }}</span>
            <span class="nav-label">{{ stage.label }}</span>
          </button>
        </nav>
      </aside>
      
      <main class="content-area">
        <div class="content-header">
          <h2 class="content-title">{{ getCurrentStageLabel() }}</h2>
        </div>
        
        <div v-if="careerContent" class="api-content">
          <div class="content-card">
            <h3 class="content-title">{{ careerTitle || '职业发展信息' }}</h3>
            <div class="content-body" v-html="careerContent"></div>
          </div>
        </div>
        
        <div v-else class="career-content">
          <div 
            v-for="(item, index) in filteredContent" 
            :key="index"
            class="career-card"
          >
            <div class="card-header">
              <span class="card-icon">{{ item.icon }}</span>
              <h3 class="card-title">{{ item.title }}</h3>
            </div>
            <p class="card-description">{{ item.description }}</p>
            <div v-if="item.details" class="card-details">
              <div v-for="(detail, idx) in item.details" :key="idx" class="detail-item">
                <span class="detail-label">{{ detail.label }}：</span>
                <span class="detail-value">{{ detail.value }}</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { getCareerInfo } from "@/api/processApi";

export default {
  name: "CareerDevelopment",
  data() {
    return {
      currentStage: 'officer',
      careerStages: [
        { value: 'officer', label: '军官发展', icon: '🎖️' },
        { value: 'soldier', label: '士兵发展', icon: '⚔️' },
        { value: 'civilian', label: '文职人员', icon: '👔' },
        { value: 'retire', label: '退役安置', icon: '🏠' }
      ],
      careerTitle: '',
      careerContent: '',
      defaultCareerData: {
        officer: [
          {
            icon: '📈',
            title: '晋升路径',
            description: '军官晋升体系清晰，从少尉到上将共10个军衔等级，通过考核和选拔逐级晋升。',
            details: [
              { label: '初级军官', value: '少尉、中尉、上尉' },
              { label: '中级军官', value: '少校、中校' },
              { label: '高级军官', value: '上校、大校' },
              { label: '将官', value: '少将、中将、上将' }
            ]
          },
          {
            icon: '🎓',
            title: '教育培训',
            description: '军官享有完善的继续教育体系，包括军事院校深造、出国留学、在职培训等多种形式。',
            details: [
              { label: '初任培训', value: '军事院校专业培训' },
              { label: '晋升培训', value: '各级军事学院进修' },
              { label: '学历提升', value: '支持攻读硕士、博士学位' }
            ]
          },
          {
            icon: '💰',
            title: '待遇保障',
            description: '军官享受国家规定的工资、津贴、补贴、住房、医疗等全方位保障。',
            details: [
              { label: '基本工资', value: '按军衔和服役年限确定' },
              { label: '岗位津贴', value: '根据岗位性质发放' },
              { label: '住房保障', value: '提供公寓或住房补贴' },
              { label: '医疗保障', value: '军队医院免费医疗' }
            ]
          },
          {
            icon: '🏠',
            title: '退役安置',
            description: '服役满一定年限后可选择转业安置或自主择业，享受相应安置政策。',
            details: [
              { label: '转业安置', value: '政府安排工作' },
              { label: '自主择业', value: '领取退役金自主就业' },
              { label: '退休待遇', value: '服役满30年可退休' }
            ]
          }
        ],
        soldier: [
          {
            icon: '⬆️',
            title: '军衔晋升',
            description: '士兵从列兵到军士长共9个等级，通过考核和选拔逐步晋升。',
            details: [
              { label: '义务兵', value: '列兵、上等兵' },
              { label: '初级军士', value: '下士、中士' },
              { label: '中级军士', value: '上士、四级军士长' },
              { label: '高级军士', value: '三级、二级、一级军士长' }
            ]
          },
          {
            icon: '🎯',
            title: '技能培养',
            description: '士兵可参加专业技能培训，考取职业资格证书，提升综合素质。',
            details: [
              { label: '岗位培训', value: '定期组织专业技能训练' },
              { label: '资格认证', value: '支持考取国家职业资格证' },
              { label: '学历教育', value: '支持报考军校或成人教育' }
            ]
          },
          {
            icon: '💵',
            title: '福利待遇',
            description: '士兵享受工资、津贴、伙食、服装等全方位保障。',
            details: [
              { label: '津贴标准', value: '按军衔和服役年限发放' },
              { label: '伙食保障', value: '免费提供营养均衡的伙食' },
              { label: '服装发放', value: '定期发放制式服装' }
            ]
          }
        ],
        civilian: [
          {
            icon: '📋',
            title: '岗位设置',
            description: '军队文职人员岗位涵盖管理、专业技术、技能保障等多个领域。',
            details: [
              { label: '管理岗', value: '负责组织协调和管理工作' },
              { label: '专业技术岗', value: '从事专业技术工作' },
              { label: '技能岗', value: '从事后勤保障等服务工作' }
            ]
          },
          {
            icon: '💼',
            title: '招聘方式',
            description: '文职人员通过公开招考、直接引进等方式录用。',
            details: [
              { label: '公开招考', value: '面向社会统一考试招聘' },
              { label: '直接引进', value: '高层次人才直接引进' },
              { label: '合同制', value: '签订劳动合同' }
            ]
          },
          {
            icon: '🎁',
            title: '薪酬福利',
            description: '文职人员实行岗位绩效工资制，享受社会保险和住房公积金。',
            details: [
              { label: '工资构成', value: '岗位工资+绩效工资+津贴补贴' },
              { label: '社会保险', value: '五险一金齐全' },
              { label: '休假制度', value: '享受国家法定节假日' }
            ]
          }
        ],
        retire: [
          {
            icon: '📊',
            title: '安置政策',
            description: '退役军人享受国家规定的安置政策，保障退役后的生活和工作。',
            details: [
              { label: '安置方式', value: '转业安置、自主择业、退休' },
              { label: '安置范围', value: '城镇退役士兵、转业军官' },
              { label: '安置保障', value: '工作安置、经济补偿、社会保障' }
            ]
          },
          {
            icon: '🎓',
            title: '教育培训',
            description: '退役军人可参加免费职业教育和技能培训，提升就业能力。',
            details: [
              { label: '学历提升', value: '支持报考高等院校' },
              { label: '技能培训', value: '免费参加职业技能培训' },
              { label: '创业扶持', value: '提供创业指导和贷款支持' }
            ]
          },
          {
            icon: '💼',
            title: '就业服务',
            description: '政府为退役军人提供就业指导、岗位推荐等就业服务。',
            details: [
              { label: '就业指导', value: '职业规划、简历制作' },
              { label: '岗位推荐', value: '定期举办专场招聘会' },
              { label: '创业支持', value: '税收减免、资金扶持' }
            ]
          }
        ]
      }
    };
  },
  mounted() {
    this.fetchCareerInfo();
  },
  computed: {
    filteredContent() {
      return this.defaultCareerData[this.currentStage] || [];
    }
  },
  methods: {
    fetchCareerInfo() {
      getCareerInfo().then(response => {
        if (response.data.code === 200) {
          const data = response.data.data;
          this.careerTitle = data.title || '军人职业发展路径';
          this.careerContent = data.pdfurl || '';
        }
      }).catch(error => {
        console.error('获取职业发展信息失败:', error);
      });
    },
    getCurrentStageLabel() {
      const stage = this.careerStages.find(s => s.value === this.currentStage);
      return stage ? stage.label : '';
    }
  }
};
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  margin-top: 80px;
  padding: 20px 12px 40px;
  background: linear-gradient(135deg, #e8f5e9 0%, #f0f4f8 100%);
  box-sizing: border-box;
}

.page-header {
  text-align: center;
  margin-bottom: 30px;
}

.page-title {
  font-size: 32px;
  color: #2e7d32;
  margin: 0 0 8px 0;
  font-weight: bold;
}

.page-subtitle {
  font-size: 16px;
  color: #666;
  margin: 0;
}

.main-content {
  display: flex;
  gap: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.sidebar {
  width: 240px;
  flex-shrink: 0;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.sidebar-header {
  padding: 20px;
  background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%);
}

.sidebar-title {
  font-size: 16px;
  color: white;
  margin: 0;
  font-weight: 600;
}

.sidebar-nav {
  padding: 12px 0;
}

.nav-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  background: transparent;
  border: none;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  transition: all 0.3s;
  text-align: left;
}

.nav-item:hover {
  background: #f5f5f5;
  color: #2e7d32;
}

.nav-item.active {
  background: #e8f5e9;
  color: #2e7d32;
  border-left: 4px solid #2e7d32;
}

.nav-icon {
  font-size: 18px;
}

.nav-label {
  flex: 1;
  text-align: left;
}

.content-area {
  flex: 1;
  min-width: 0;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.content-title {
  font-size: 20px;
  color: #333;
  margin: 0;
  font-weight: 600;
}

.career-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.career-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s, box-shadow 0.2s;
}

.career-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.card-icon {
  font-size: 32px;
}

.card-title {
  font-size: 18px;
  color: #333;
  margin: 0;
  font-weight: 600;
}

.card-description {
  font-size: 15px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 16px 0;
}

.card-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  padding-top: 16px;
  border-top: 1px dashed #eee;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 13px;
  color: #999;
}

.detail-value {
  font-size: 14px;
  color: #2e7d32;
  font-weight: 500;
}

@media (max-width: 768px) {
  .main-content {
    flex-direction: column;
  }
  
  .sidebar {
    width: 100%;
  }
  
  .nav-item {
    padding: 12px 16px;
  }
  
  .page-title {
    font-size: 26px;
  }
  
  .content-title {
    font-size: 18px;
  }
  
  .card-details {
    grid-template-columns: 1fr;
  }
}

.api-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.content-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.content-card .content-title {
  font-size: 20px;
  color: #333;
  margin: 0 0 16px 0;
  font-weight: 600;
}

.content-body {
  font-size: 15px;
  color: #333;
  line-height: 1.8;
}

.content-body h4 {
  font-size: 16px;
  color: #2e7d32;
  margin: 20px 0 10px 0;
  font-weight: 600;
}

.content-body p {
  margin: 0 0 12px 0;
}

.content-body ul, .content-body ol {
  margin: 0 0 12px 20px;
  padding: 0;
}

.content-body li {
  margin-bottom: 8px;
}
</style>